import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Chart } from '../../shared/charts/chart';
import { Stats, TimeToFirstContact } from '../stats.model';

@Component({
  selector: 'cc-time-to-first-contact-chart',
  templateUrl: './time-to-first-contact-chart.component.html',
  styleUrls: ['./time-to-first-contact-chart.component.scss']
})
export class TimeToFirstContactChartComponent implements OnChanges {
  @Input() public stats: Stats;

  private readonly intervals = [1, 2, 3, 5, 10, 15, 25, 45, 45];
  private readonly labels = this.intervals.map(
    (interval, index) => (this.isLastInterval(index) ? `>${interval}min` : `<${interval}min`)
  );

  public chartData: Chart[] = [
    {
      labels: this.labels,
      data: [],
      label: 'Time to first contact',
      backgroundColor: Array.from({ length: this.labels.length }, () => '#50A89D')
    }
  ];

  get dataAvailable(): boolean {
    return this.stats && this.stats.timesToFirstContact && this.stats.timesToFirstContact.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dataAvailable) {
      this.changeChartData(this.stats.timesToFirstContact, this.stats.leads);
    } else {
      this.showNoDataAvailable();
    }
  }

  private showNoDataAvailable() {
    // 'random' data
    this.changeChartData(
      [
        { minute: 1, numberOfContacts: 127 },
        { minute: 2, numberOfContacts: 100 },
        { minute: 4, numberOfContacts: 93 },
        { minute: 8, numberOfContacts: 43 },
        { minute: 13, numberOfContacts: 33 },
        { minute: 513, numberOfContacts: 3 }
      ],
      430
    );
  }

  private changeChartData(timesToFirstContact: TimeToFirstContact[], leadsCount: number) {
    const copy: Chart[] = [...this.chartData];

    const predicate = index => (this.isLastInterval(index) ? this.sumAllContacts : this.sumContactsBeforeInterval);

    copy[0].data = this.intervals
      .map((interval, index) => this.sumContactsPerInterval(timesToFirstContact, interval, predicate(index)))
      .map(sum => sum * 100 / leadsCount);

    this.chartData = copy;
  }

  private sumAllContacts() {
    return true;
  }

  private sumContactsBeforeInterval(minute, interval) {
    return minute <= interval;
  }

  private isLastInterval(index: number): boolean {
    return index === this.intervals.length - 1;
  }

  private sumContactsPerInterval(
    timesToFirstContact: TimeToFirstContact[],
    interval: number,
    predicate: (a, b) => boolean
  ) {
    return timesToFirstContact
      .filter(({ minute }) => predicate(minute, interval))
      .reduce((sum, { numberOfContacts }) => sum + numberOfContacts, 0);
  }

  chartConfigMapper(defaultConfig) {
    const config = { ...defaultConfig };

    config.tooltips.callbacks.title = () => '';
    config.tooltips.callbacks.label = (tooltipItem, data) => {
      const min = data.datasets[0].labels[tooltipItem.index];
      const percent = Number(data.datasets[0].data[tooltipItem.index]).toFixed(1) + '%';
      const isLast = tooltipItem.index === data.datasets[0].data.length - 1;
      return `${percent} of leads were contacted ${isLast ? 'in more than' : 'in less than'} ${min.substring(1)}`;
    };

    config.scales.xAxes[0].barPercentage = 1.0;

    config.plugins.datalabels.formatter = value => Number(value).toFixed(1) + '%';
    config.plugins.datalabels.font = { size: 11 };

    return config;
  }
}
